<script setup lang="ts">
import type { SignupOffer } from '@promotion/types'
import { useCookiebotConsent } from '#imports'

interface GameCard {
    title: string
    src: string
    bg: string
    asset: string
    logo: string
    slug: string
    videoId?: string
}

interface HeaderFallbackData {
    tagline?: string
    headline?: string
    description?: string
    backgroundColor?: string
    backgroundImage?: string
    backgroundImageMobile?: string
    backgroundVideo?: string
    backgroundVideoDesktop?: string
    backgroundVideoPoster?: string
    buttonText?: string
    featuredImage?: string
    featuredVideo?: string
    splashImage?: string
    usp_1?: string
    usp_2?: string
    usp_3?: string
    trustpilotEnabled?: boolean
    gameCards?: GameCard[]
}

interface HeaderProps {
    offers: SignupOffer[]
    content: HeaderFallbackData
    backgroundColor?: string
}

const props = defineProps<HeaderProps>()

const route = useRoute()
const links = useLinks()
const { isFromPartner } = usePartner()
const { start } = useGame()
const { trackEvent } = useTracking()
const { isConsentReady } = useCookiebotConsent()
const { defaultBundle } = useSignupBundles()

const gameLoading = ref(false)

async function startGame(slug: string) {
    if (gameLoading.value || !slug) return

    gameLoading.value = true

    trackEvent({
        event_name: `landing-page-header-game-start`,
        event_type: 'Click',
        meta_data: [{
            category: 'Games',
            gameSlug: slug
        }]
    }) as void

    await start({ gameSlug: slug })

    gameLoading.value = false
}

// Card rotation state (starts at 3 so first rotation moves to index 0)
const rotationIndex = ref(0)
const isAnimating = ref(false)
const hasRotatedOnce = ref(false)

function rotateCards(direction: 'forward' | 'backward' = 'forward', isAutomated = false) {
    // Guard: prevent rotation while cards are animating
    if (isAnimating.value) return

    isAnimating.value = true

    // % is used to keep the rotationIndex value cycling within the range of 0-3, creating a circular/wrapping behavior
    // ex. rotationIndex = 3: (3 + 1) % 4 = 0 ← wraps back to 0
    if (direction === 'forward') {
        rotationIndex.value = (rotationIndex.value + 1) % 4
    }
    else {
        rotationIndex.value = (rotationIndex.value - 1 + 4) % 4
    }

    if (!isAutomated) {
        const activeCardIndex = (1 - rotationIndex.value + 4) % 4
        const activeGame = props.content.gameCards?.[activeCardIndex]

        if (activeGame) {
            trackEvent({
                event_name: 'landing-page-header-game-changed',
                event_type: 'Click',
                meta_data: [{
                    category: 'Games',
                    gameSlug: activeGame.slug
                }]
            })
        }
    }

    // Reset animation guard after transition duration (1.2s from CSS)
    setTimeout(() => {
        isAnimating.value = false
        hasRotatedOnce.value = true
    }, 1200)
}

// Calculate card positions based on rotation index
const cardPositions = computed(() => {
    const positions = ['--rotate-pos-1', '--rotate-pos-2', '--rotate-pos-3', '--rotate-pos-4']
    const zIndexes = [10, 20, 10, 0]

    return [1, 2, 3, 4].map((cardNum) => {
        const posIndex = (cardNum - 1 + rotationIndex.value) % 4

        return {
            position: positions[posIndex],
            zIndex: zIndexes[posIndex],
            isActive: positions[posIndex] === '--rotate-pos-2'
        }
    })
})

function handleCardClick(index: number, slug: string) {
    // Calculate which position this card is currently in
    const currentPosIndex = (index + rotationIndex.value) % 4

    // If card is in index 0 (--rotate-pos-1, left-most) - rotate forward
    if (currentPosIndex === 0) {
        rotateCards('forward')
        return
    }

    // If card is in index 2 (--rotate-pos-3, right-most) - rotate backward
    if (currentPosIndex === 2) {
        rotateCards('backward')
        return
    }

    // Middle cards - start game
    startGame(slug)
}

// Touch/swipe handling with VueUse
const visualsElement = ref<HTMLElement>()
const { direction } = useSwipe(visualsElement, {
    threshold: 50,
    onSwipeEnd() {
        if (direction.value === 'left') {
            rotateCards('backward')
        }
        else if (direction.value === 'right') {
            rotateCards('forward')
        }
    }
})

const hasScrolled = ref(false)
watch(isConsentReady, (newValue) => {
    if (newValue && !hasScrolled.value) {
        rotateCards('forward', true)
    }
}, { immediate: true })

useEventListener('scroll', () => {
    if (!hasRotatedOnce.value) rotateCards('forward', true)
    hasScrolled.value = true
}, { once: true, passive: true })
</script>

<template>
    <header
        class="header"
        :class="{
            'header--terms-space': !isFromPartner,
            'header--scrolled': hasScrolled
        }"
    >
        <div class="header__wrap">
            <div v-if="content" class="header__offer">
                <!-- eslint-disable-next-line vue/no-v-html The content of context.headline is safe -->
                <h1 class="header__title" v-html="defaultBundle?.title" />

                <UIButton
                    v-bind="links.signup.props()"
                    class="header__button"
                    as="cta"
                    size="xl"
                    block
                    shiny="snake"
                >
                    FORTSÆT

                    <template #append>
                        <UIIcon name="arrow-right" size="9" />
                    </template>
                </UIButton>

                <!-- eslint-disable-next-line vue/no-v-html -->
                <p class="header__subtitle" v-html="defaultBundle?.subtitle" />
            </div>

            <div
                ref="visualsElement"
                class="header__visuals"
            >
                <NuxtImg
                    class="glow"
                    src="/media/landing-page/effects/glow.png"
                    alt="Circular glow effect"
                    provider="cloudflare"
                    preset="local"
                    width="514"
                    height="514"
                    densities="1"
                    loading="lazy"
                    decoding="async"
                    quality="64"
                />
                <div
                    v-for="(card, index) in content.gameCards"
                    :key="index"
                    :class="['header__game-card',
                             { 'header__game-card--video-on': cardPositions[index]?.isActive && hasRotatedOnce && card.videoId }
                    ]"
                    :style="{
                        transform: `var(${cardPositions[index]?.position})`,
                        zIndex: cardPositions[index]?.zIndex
                    }"
                    @click.prevent="handleCardClick(index, card.slug)"
                >
                    <NuxtImg
                        v-if="card.src"
                        class="header__game-card-image fit-parent"
                        :src="card.src"
                        :alt="card.title"
                        provider="cloudflare"
                        preset="local"
                        sizes="180px sm:220px xl:400px"
                        width="180"
                        height="198"
                        :loading="index < 2 ? 'eager' : 'lazy'"
                        :fetchpriority="index < 2 ? 'high' : undefined"
                        decoding="async"
                    />
                    <div v-else class="header__game-card-content">
                        <NuxtImg
                            class="header__game-card-bg fit-parent"
                            :src="card.bg"
                            :alt="`${card.title} image`"
                            provider="cloudflare"
                            preset="gameservice"
                            sizes="164px sm:210px lg:400px"
                            width="164"
                            height="190"
                            fit="cover"
                            decoding="async"
                            :loading="index < 2 ? 'eager' : 'lazy'"
                            :fetchpriority="index < 2 ? 'high' : undefined"
                        />
                        <UIIframe
                            :video-id="card.videoId"
                            :load="!!(card.videoId && cardPositions[index]?.isActive && hasRotatedOnce)"
                            :preload="index === 0 ? true : false"
                            loading="eager"
                            class="header__game-card-iframe"
                        />
                        <span class="fit-parent header__game-card-frame" />
                        <NuxtImg
                            class="header__game-card-asset"
                            :src="card.asset"
                            :alt="`${card.title} asset`"
                            provider="cloudflare"
                            preset="local"
                            sizes="120px md:240px"
                            width="120"
                            height="228"
                            fit="cover"
                            loading="lazy"
                            fetchpriority="high"
                            decoding="async"
                        />
                        <NuxtImg
                            class="header__game-card-logo"
                            :src="card.logo"
                            :alt="card.title"
                            provider="cloudflare"
                            preset="gameservice"
                            sizes="140px md:220px"
                            width="140"
                            height="140"
                            fit="contain"
                            loading="lazy"
                            fetchpriority="high"
                            decoding="async"
                        />
                    </div>
                </div>

                <div class="header__indicators">
                    <span
                        v-for="index in 4"
                        :key="index"
                        class="header__indicator"
                        :class="{ 'header__indicator--active': rotationIndex === index - 1 }"
                    />
                </div>
            </div>
        </div>

        <div class="header__bg fit-parent">
            <div class="header__pattern fit-parent" />
        </div>
    </header>
</template>

<style scoped lang="scss">
.header {
    --rotate-pos-1: translate3d(-80%, 20px, -200px) rotate(-19deg);
    --rotate-pos-2: translate3d(0, 0, 100px) rotate(-3deg);
    --rotate-pos-3: translate3d(80%, 20px, -200px) rotate(6.6deg);
    --rotate-pos-4: translate3d(0, 0, -300px) rotate(-1.5deg);

    position: relative;
    width: 100%;
    overflow: hidden;

    a:not(.btn--cta) {
        color: var(--on-background);
    }

    &__wrap {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: repeat(3, min-content);
        text-align: center;
        align-items: center;
        justify-items: center;
        min-height: clamp(450px, 55vh, 520px);
        max-width: 1280px;
        margin-top: clamp(50px, 12vh, 100px);
        margin-bottom: 60px;
        margin-left: auto;
        margin-right: auto;

        color: var(--on-background);

        position: relative;
        z-index: 1;

        @media (min-width: $screen-md) {
            margin-top: 100px;
            min-height: clamp(450px, 55vh, 600px);
        }

        @media (min-width: $screen-lg) {
            grid-template-columns: 1fr 2fr;
            grid-template-rows: repeat(2, min-content);
            gap: 60px 30px;
            justify-items: unset;
            margin: 160px auto 0;

            text-align: left;
        }
    }

    &__offer,
    &__visuals,
    &__indicators,
    &__terms {
        transition: all 1s $easeOutQuad;
    }

    &__visuals,
    &__terms {
        .header--scrolled & {
            transform: translate3d(0, 40px, 0);
        }
    }

    &__offer {
        position: relative;
        z-index: 1;

        padding: 0 15px;
        max-width: 440px;

        .header--scrolled & {
            transform: translate3d(0, 50px, 0);
        }
    }

    &__title {
        font-size: 11.2vw;
        font-size: clamp(34px, 11.2vw, 52px);
        font-weight: 900;
        font-family: $brand-font-family;
        line-height: 1.025;
        margin-bottom: 20px;
        opacity: 0;
        animation: appear 0.3s $easeOutQuad 0.4s forwards;

        @media (min-width: $screen-lg) {
            font-size: 44px;
        }

        @media (min-width: $screen-xl) {
            font-size: 50px;
        }

        :deep(.text--featured) {
            white-space: nowrap;
            display: block;
        }
    }

    &__subtitle {
        font-size: $font-size-xl;
        font-weight: $text-bold;
        margin-top: 10px;
    }

    &__terms {
        grid-row: 3 / 4;
        z-index: 1;

        padding: 0 15px;
        text-align: center;

        @media (min-width: $screen-lg) {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
        }
    }

    &__button {
        height: 60px;
        max-width: 330px;
        opacity: 0;
        animation: appear 0.5s $easeOutQuad 0.6s forwards;
    }

    :deep(.offer-terms) {
        margin: 25px 0 115px; // 95px margin added to make room for .header__offer scroll animation
    }

    &__visuals {
        position: relative;
        display: flex;
        width: 100%;
        justify-content: center;
        align-items: center;
        perspective: 1000px;
        min-height: 200px;
        // max-height: 40vh;
        margin-bottom: 40px;
        opacity: 0;
        animation: appear 0.7s $easeOutQuad 0.2s forwards;

        grid-row: 1 / 2;

        .glow {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
            animation: appear 1s $easeOutQuad 0.8s forwards;
            opacity: 0;

            @media (min-width: $screen-lg) {
                height: 1028px;
                width: 1028px;
            }
        }

        @media (min-width: $screen-sm) {
            min-height: 240px;
        }

        @media (min-width: $screen-md) {
            // width: 58%;
            min-height: 330px;
            margin-bottom: 60px;
        }

        @media (min-width: $screen-lg) {
            grid-column: 2 / 3;
        }
    }

    &__game-card {
        position: absolute;
        width: 50%;
        aspect-ratio: 0.9 / 1;
        cursor: pointer;
        transition: all 1.2s $easeOutQuart;

        // Remove tap highlight on mobile
        -webkit-tap-highlight-color: transparent;
        user-select: none;

        @media (min-width: $screen-sm) {
            width: 56%;
        }

        @media (min-width: $screen-md) {
            width: 52%;
            max-width: 36vw;
        }

        @media (min-width: $screen-xl) {
            width: 400px;
        }

        &-bg {
            border-radius: inherit;
        }

        &-content {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 72%;
            height: 78%;
            border-radius: 10%;
            opacity: 1;
            transform: translate(-50%, -50%);
            transition-delay: 0s;
            pointer-events: none;
        }

        &-content,
        &-logo,
        &-asset {
            transition:
                opacity 0.4s $easeOutExpo,
                border-radius 0.4s $easeOutExpo,
                width 0.8s $easeOutExpo,
                transform 0.8s $easeOutExpo;
        }

        &-asset,
        &-logo {
            position: absolute;
            object-fit: contain;
            transition-delay: 0s;
        }

        &-frame {
            border-radius: inherit;
            border: 6px solid rgba(255, 255, 255, 0.2);
        }

        &-iframe {
            position: absolute;
            opacity: 0;
            transition: opacity 0.5s $easeOutExpo;
            transition-delay: 0s;
        }

        &-asset {
            left: -20%;
            top: 50%;
            transform: translate(0, -50%);
            width: auto;
            height: 116%;
        }

        &-logo {
            height: auto;
            top: 10%;
            right: 0;
            max-width: 86%;
            aspect-ratio: 1;
            transform: translate(8%, 30%);

            @media (min-width: $screen-md) {
                width: 220px;
            }
        }

        &--video-on {
            .header__game-card-asset {
                transform: translate(15%, -50%);
                transition-delay: 0.3s;
            }

            .header__game-card-content {
                width: 148%;
                transform: translate(-50%, -50%) scale(1, 1);
                transition-delay: 0.3s;
                border-radius: 20px;
            }

            .header__game-card-iframe {
                opacity: 1;
                transition-delay: 0.4s;
            }

            .header__game-card-logo {
                transform: translate(30%, -54%) scale(0.6);
                transition-delay: 0.3s;
            }
        }
    }

    &__indicators {
        position: absolute;
        top: 100%;
        left: 50%;
        display: flex;
        gap: 10px;
        justify-content: center;
        align-items: center;
        transform: translate3d(-50%, 20px, 0);
        z-index: 4;
        opacity: 0;
        animation: appear 1s $easeOutQuad 1.5s forwards;

        @media (min-width: $screen-md) {
            transform: translate3d(-50%, 20px, 0);
        }

        @media (min-width: $screen-lg) {
            transform: translate3d(-50%, 80px, 0);
        }
    }

    &__indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--on-background);
        opacity: 0.4;
        transition: opacity 0.3s ease;

        &--active {
            opacity: 1;
        }

        @media (min-width: $screen-md) {
            width: 10px;
            height: 10px;
        }
    }

    &__bg {
        z-index: 0;
        object-position: top center;
        background: linear-gradient(180deg, var(--background) 20%, #17894e 75%);

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 40%;
            background: linear-gradient(to bottom, transparent 0%, transparent 30%, #041135 100%);
        }
    }

    &__pattern {
        mix-blend-mode: overlay;
        opacity: 0.07;
        background-image: url('/media/landing-page/repeatable-carpet-tile.svg');
        background-repeat: repeat;
    }
}
</style>
